import { useState, useEffect, useCallback } from "react";
import { ItemImage } from "@/lib/types";
import imageUtils from "@/lib/imageUtils";
import { useAuth } from "@/contexts/AuthContext"; // Assuming you have an auth context

interface UseItemImagesProps {
  itemId: string;
  forcePermission?: boolean; // Allow forcing permission for new item creation
}

interface UseItemImagesReturn {
  images: ItemImage[];
  mainImage: ItemImage | null;
  isLoading: boolean;
  error: string | null;
  uploadImage: (
    file: File,
    caption?: string,
    isMain?: boolean,
  ) => Promise<ItemImage | null>;
  deleteImage: (imageId: string) => Promise<boolean>;
  setMainImage: (imageId: string) => Promise<boolean>;
  reorderImages: (imageIds: string[]) => Promise<boolean>;
  canManage: boolean;
  refetch: () => Promise<void>;
}

export default function useItemImages({
  itemId,
  forcePermission = false,
}: UseItemImagesProps): UseItemImagesReturn {
  const [images, setImages] = useState<ItemImage[]>([]);
  const [mainImage, setMainImage] = useState<ItemImage | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [canManage, setCanManage] = useState<boolean>(forcePermission);
  const { user } = useAuth();

  // Fetch all images for the item
  const fetchImages = useCallback(async () => {
    if (!itemId) return;

    try {
      setIsLoading(true);
      setError(null);

      const fetchedImages = await imageUtils.getItemImages(itemId);
      setImages(fetchedImages);

      // Find the main image
      const mainImg = fetchedImages.find((img) => img.isMain) || null;
      setMainImage(mainImg);

      // Check if the user can manage these images
      // If forcePermission is true, skip the check and always allow management
      if (forcePermission) {
        setCanManage(true);
      } else if (user?.id) {
        const hasPermission = await imageUtils.canManageItemImages(
          user.id,
          itemId,
        );
        setCanManage(hasPermission);
      } else {
        setCanManage(false);
      }
    } catch (err) {
      console.error("Error fetching images:", err);
      setError("Failed to load images");
    } finally {
      setIsLoading(false);
    }
  }, [itemId, user, forcePermission]);

  // Upload a new image
  const uploadImage = useCallback(
    async (
      file: File,
      caption?: string,
      isMain?: boolean,
    ): Promise<ItemImage | null> => {
      if (!itemId || (!canManage && !forcePermission)) {
        setError("You do not have permission to upload images");
        return null;
      }

      try {
        setError(null);

        const uploadedImage = await imageUtils.uploadItemImage({
          file,
          itemId,
          caption,
          isMain: isMain || images.length === 0, // Make it main if it's the first image
        });

        if (uploadedImage) {
          // Refetch to get the updated list
          await fetchImages();
          return uploadedImage;
        } else {
          setError("Failed to upload image");
          return null;
        }
      } catch (err) {
        console.error("Error uploading image:", err);
        setError("An error occurred while uploading the image");
        return null;
      }
    },
    [itemId, canManage, images.length, fetchImages, forcePermission],
  );

  // Delete an image
  const deleteImage = useCallback(
    async (imageId: string): Promise<boolean> => {
      if (!itemId || (!canManage && !forcePermission)) {
        setError("You do not have permission to delete images");
        return false;
      }

      try {
        setError(null);

        const success = await imageUtils.deleteItemImage(imageId);

        if (success) {
          // Refetch to get the updated list
          await fetchImages();
          return true;
        } else {
          setError("Failed to delete image");
          return false;
        }
      } catch (err) {
        console.error("Error deleting image:", err);
        setError("An error occurred while deleting the image");
        return false;
      }
    },
    [itemId, canManage, fetchImages, forcePermission],
  );

  // Set an image as the main image
  const setAsMainImage = useCallback(
    async (imageId: string): Promise<boolean> => {
      if (!itemId || (!canManage && !forcePermission)) {
        setError("You do not have permission to update images");
        return false;
      }

      try {
        setError(null);

        const success = await imageUtils.setMainImage(imageId, itemId);

        if (success) {
          // Refetch to get the updated list
          await fetchImages();
          return true;
        } else {
          setError("Failed to set main image");
          return false;
        }
      } catch (err) {
        console.error("Error setting main image:", err);
        setError("An error occurred while setting the main image");
        return false;
      }
    },
    [itemId, canManage, fetchImages, forcePermission],
  );

  // Reorder images
  const reorderImages = useCallback(
    async (imageIds: string[]): Promise<boolean> => {
      if (!itemId || (!canManage && !forcePermission)) {
        setError("You do not have permission to reorder images");
        return false;
      }

      try {
        setError(null);

        const success = await imageUtils.reorderItemImages(itemId, imageIds);

        if (success) {
          // Refetch to get the updated list
          await fetchImages();
          return true;
        } else {
          setError("Failed to reorder images");
          return false;
        }
      } catch (err) {
        console.error("Error reordering images:", err);
        setError("An error occurred while reordering images");
        return false;
      }
    },
    [itemId, canManage, fetchImages, forcePermission],
  );

  // Initial fetch
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchImages();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [fetchImages]);

  // Update permissions when user changes
  useEffect(() => {
    if (forcePermission) {
      const timer = window.setTimeout(() => {
        setCanManage(true);
      }, 0);
      return () => window.clearTimeout(timer);
    } else if (user?.id && itemId) {
      imageUtils
        .canManageItemImages(user.id, itemId)
        .then((hasPermission) => {
          setCanManage(hasPermission);
        })
        .catch((err) => {
          console.error("Error checking permissions:", err);
          setCanManage(false);
        });
    } else {
      const timer = window.setTimeout(() => {
        setCanManage(false);
      }, 0);
      return () => window.clearTimeout(timer);
    }
  }, [user, itemId, forcePermission]);

  return {
    images,
    mainImage,
    isLoading,
    error,
    uploadImage,
    deleteImage,
    setMainImage: setAsMainImage,
    reorderImages,
    canManage,
    refetch: fetchImages,
  };
}
